import React from "react";
import { useTheme } from "@mui/material/styles";

/* ───────────────────────── COMMON SKELETON ───────────────────────── */

const SkeletonBox = ({ w = "100%", h = 12, r = 6 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <div
      style={{
        width: w,
        height: h,
        borderRadius: r,
        background: isDark
          ? "linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)"
          : "linear-gradient(90deg, rgba(0,0,0,0.05) 25%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.05) 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite linear",
      }}
    />
  );
};

const SkeletonPanel = ({ children }) => {
  const theme = useTheme();
  const t = theme.custom;

  return (
    <div
      style={{
        background: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
};

const PanelHeader = () => {
  const theme = useTheme();
  const t = theme.custom;

  return (
    <div style={{ padding: 14, borderBottom: `1px solid ${t.border}` }}>
      <SkeletonBox w={100} />
    </div>
  );
};

/* ───────────────────────── TABLE ───────────────────────── */

const TableHeader = () => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "2fr 1fr 1.5fr 1fr 0.7fr 1fr 1fr 1fr",
      gap: 12,
      padding: 12,
    }}
  >
    {Array.from({ length: 8 }).map((_, i) => (
      <SkeletonBox key={i} />
    ))}
  </div>
);

const TableRow = () => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "2fr 1fr 1.5fr 1fr 0.7fr 1fr 1fr 1fr",
      gap: 12,
      padding: 12,
    }}
  >
    {Array.from({ length: 8 }).map((_, i) => (
      <SkeletonBox key={i} />
    ))}
  </div>
);

const TableSkeleton = () => {
  const theme = useTheme();
  const t = theme.custom;

  return (
    <SkeletonPanel>
      <div style={{ padding: 14, borderBottom: `1px solid ${t.border}` }}>
        <SkeletonBox w={120} />
      </div>

      <TableHeader />

      {Array.from({ length: 6 }).map((_, i) => (
        <TableRow key={i} />
      ))}

      <div style={{ padding: 12 }}>
        <SkeletonBox w={150} />
      </div>
    </SkeletonPanel>
  );
};

/* ───────────────────────── KPI ───────────────────────── */

const KpiCard = () => (
  <SkeletonPanel>
    <div style={{ padding: 16 }}>
      <SkeletonBox w="60%" />
      <div style={{ marginTop: 10 }}>
        <SkeletonBox w="40%" h={24} />
      </div>
    </div>
  </SkeletonPanel>
);

const KpiGrid = () => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
    {Array.from({ length: 4 }).map((_, i) => (
      <KpiCard key={i} />
    ))}
  </div>
);

/* ───────────────────────── CHARTS ───────────────────────── */

const DonutSkeleton = () => (
  <SkeletonPanel>
    <PanelHeader />
    <div
      style={{
        padding: 16,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <SkeletonBox w={100} h={100} r="50%" />
      <SkeletonBox w="80%" />
      <SkeletonBox w="60%" />
    </div>
  </SkeletonPanel>
);

const BarChartSkeleton = () => (
  <SkeletonPanel>
    <PanelHeader />
    <div style={{ padding: 16 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <SkeletonBox w={`${80 - i * 10}%`} h={14} />
        </div>
      ))}
    </div>
  </SkeletonPanel>
);

/* ───────────────────────── MAIN ───────────────────────── */

const ReportSkeleton = () => {
  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 320px",
          gap: 16,
        }}
      >
        {/* LEFT */}
        <TableSkeleton />

        {/* RIGHT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <KpiGrid />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <DonutSkeleton />
            <DonutSkeleton />
          </div>

          <BarChartSkeleton />
        </div>
      </div>
    </>
  );
};

export default ReportSkeleton;